import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../../api/axiosInstance';

const emptyConfig = {
  enabled: false,
  apiUrl: 'http://172.16.0.30:80/centreon',
  username: '',
  password: '',
};

function formatDate(value) {
  if (!value) return 'Jamais';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function CentreonSettingsPage() {
  const [config, setConfig] = useState(emptyConfig);
  const [hostgroups, setHostgroups] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hostgroupsLoading, setHostgroupsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadConfig = async () => {
    const { data } = await axios.get('/api/settings/centreon/config');
    setConfig((current) => ({ ...emptyConfig, ...data, password: current.password || '' }));
  };

  const loadHostgroups = async ({ refresh = false } = {}) => {
    setHostgroupsLoading(true);
    try {
      const { data } = await axios.get(`/api/settings/centreon/hostgroups?refresh=${refresh}`);
      setOrganizations(data.organizations || []);
      setHostgroups((data.hostgroups || []).map((hostgroup) => ({
        ...hostgroup,
        organization: hostgroup.mapping?.organization || null,
        suggestedOrganization: hostgroup.suggestedOrganization || null,
        enabled: hostgroup.mapping?.enabled ?? true,
        notes: hostgroup.mapping?.notes || '',
        suggested: Boolean(hostgroup.suggestedOrganization && !hostgroup.mapping?.organization),
      })));
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || error.response?.data?.error || 'Impossible de charger les hostgroups Centreon.' });
    } finally {
      setHostgroupsLoading(false);
    }
  };

  const hydrate = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadConfig(), loadHostgroups()]);
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger Centreon.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const saveConfig = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axios.put('/api/settings/centreon/config', {
        enabled: config.enabled,
        apiUrl: config.apiUrl,
        username: config.username,
        password: config.password,
      });
      setConfig({ ...data, password: '' });
      setFeedback({ type: 'success', message: 'Configuration Centreon sauvegardée.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Sauvegarde Centreon impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const testConfig = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      const { data } = await axios.post('/api/settings/centreon/test');
      setConfig((current) => ({ ...current, ...data, password: '' }));
      setFeedback({ type: 'success', message: data.lastTestMessage || 'Connexion Centreon OK.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.error || error.response?.data?.message || 'Connexion Centreon impossible.' });
    } finally {
      setTesting(false);
    }
  };

  const saveMappings = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = hostgroups.map((hostgroup) => ({
        centreonHostgroupId: hostgroup.id,
        centreonHostgroupName: hostgroup.name,
        alias: hostgroup.alias,
        organization: hostgroup.organization?._id || null,
        enabled: hostgroup.enabled,
        notes: hostgroup.notes,
      }));
      await axios.put('/api/settings/centreon/mappings', { mappings: payload });
      setFeedback({ type: 'success', message: 'Mapping Centreon sauvegardé.' });
      await loadHostgroups();
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de sauvegarder le mapping Centreon.' });
    } finally {
      setSaving(false);
    }
  };

  const updateHostgroup = (id, patch) => {
    setHostgroups((current) => current.map((hostgroup) => hostgroup.id === id ? { ...hostgroup, ...patch } : hostgroup));
  };

  const columns = useMemo(() => [
    { accessorKey: 'name', header: 'Hostgroup Centreon', size: 260 },
    { accessorKey: 'alias', header: 'Alias', size: 180 },
    {
      accessorKey: 'organization.name',
      header: 'Client',
      size: 320,
      Cell: ({ row }) => (
        <Stack spacing={0.75}>
          <Autocomplete
            size="small"
            options={organizations}
            value={row.original.organization || null}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            onChange={(_, value) => updateHostgroup(row.original.id, { organization: value, suggested: false })}
            renderInput={(params) => <TextField {...params} placeholder="Aucun client" />}
          />
          {!row.original.organization?._id && row.original.suggestedOrganization?._id ? (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" color="info" variant="outlined" label={`Suggestion : ${row.original.suggestedOrganization.name}`} />
              <Button size="small" variant="text" onClick={() => updateHostgroup(row.original.id, { organization: row.original.suggestedOrganization, enabled: true, suggested: false })}>
                Utiliser
              </Button>
              <Button size="small" color="inherit" variant="text" onClick={() => updateHostgroup(row.original.id, { organization: null, enabled: false, suggested: false })}>
                Ignorer
              </Button>
            </Stack>
          ) : null}
        </Stack>
      ),
    },
    {
      accessorKey: 'mappingStatus',
      header: 'Statut',
      size: 110,
      Cell: ({ row }) => {
        if (row.original.enabled === false) return <Chip size="small" color="default" label="Ignoré" />;
        if (row.original.organization?._id) return <Chip size="small" color="success" label="Mappé" />;
        if (row.original.suggestedOrganization?._id) return <Chip size="small" color="info" variant="outlined" label="Suggestion" />;
        return <Chip size="small" color="warning" label="À mapper" />;
      },
    },
    {
      accessorKey: 'enabled',
      header: 'Mapper',
      size: 80,
      Cell: ({ row }) => (
        <Switch checked={Boolean(row.original.enabled)} onChange={(event) => updateHostgroup(row.original.id, { enabled: event.target.checked })} />
      ),
    },
    {
      accessorKey: 'notes',
      header: 'Note',
      size: 220,
      Cell: ({ row }) => (
        <TextField size="small" fullWidth value={row.original.notes || ''} onChange={(event) => updateHostgroup(row.original.id, { notes: event.target.value })} />
      ),
    },
  ], [organizations]);

  const table = useMaterialReactTable({
    columns,
    data: hostgroups,
    localization: MRT_Localization_FR,
    enableDensityToggle: false,
    enableFullScreenToggle: true,
    enableColumnFilters: true,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      density: 'compact',
      pagination: { pageSize: 20, pageIndex: 0 },
    },
    state: { isLoading: hostgroupsLoading },
  });

  if (loading) return <Typography color="text.secondary">Chargement Centreon...</Typography>;

  return (
    <Stack spacing={2}>
      {feedback ? <Alert severity={feedback.type} onClose={() => setFeedback(null)}>{feedback.message}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Centreon</Typography>
              <Typography variant="body2" color="text.secondary">
                Connexion API REST/CLAPI et mapping des hostgroups vers les clients.
              </Typography>
            </Box>
            <Chip color={config.enabled ? 'success' : 'default'} label={config.enabled ? 'Activé' : 'Inactif'} />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField size="small" label="URL Centreon" value={config.apiUrl || ''} onChange={(event) => setConfig((current) => ({ ...current, apiUrl: event.target.value }))} sx={{ minWidth: 300 }} />
            <TextField size="small" label="Utilisateur" value={config.username || ''} onChange={(event) => setConfig((current) => ({ ...current, username: event.target.value }))} sx={{ minWidth: 180 }} />
            <TextField size="small" label={config.hasPassword ? `Mot de passe (${config.passwordMasked})` : 'Mot de passe'} type="password" value={config.password || ''} onChange={(event) => setConfig((current) => ({ ...current, password: event.target.value }))} sx={{ minWidth: 220 }} />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Actif</Typography>
              <Switch checked={Boolean(config.enabled)} onChange={(event) => setConfig((current) => ({ ...current, enabled: event.target.checked }))} />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={saveConfig} disabled={saving}>Sauvegarder</Button>
            <Button variant="outlined" onClick={testConfig} disabled={testing || saving}>{testing ? <CircularProgress size={18} /> : 'Tester'}</Button>
            <Button variant="outlined" onClick={() => loadHostgroups({ refresh: true })} disabled={hostgroupsLoading}>Charger les hostgroups</Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Dernier test : {formatDate(config.lastTestAt)} {config.lastTestMessage ? `- ${config.lastTestMessage}` : ''}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>Mapping hostgroups</Typography>
          <Button variant="contained" onClick={saveMappings} disabled={saving || hostgroupsLoading}>Sauvegarder le mapping</Button>
        </Stack>
        <MaterialReactTable table={table} />
      </Paper>
    </Stack>
  );
}
